import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { LoginProjectConfiguration } from '../../../api';
import { ChecklistItems } from '../../../api/configurations/checklist/types';
import { calbackURLValidatation, CallbackUrlSettings } from '../../../components';
import { ConfigurationSample, Preloader, ToDashboardError } from '../../../components/ui';
import { useConfig } from '../../../contexts/config';
import { useChecklist, useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';

type RouteParams = { projectId: string; confPage: string };

const CallbackUrlSettingsPage = ({ match }: RouteComponentProps): JSX.Element | null => {
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;
  const { enableChecklistItem } = useChecklist(projectId);
  const {
    currentProjectFetching,
    currentProjectType,
    currentProject,
    currentUpdateFetching,
    updateCurrentProject,
    error
  } = useCurrentProject(projectId);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const { xsollaAnalytics } = useConfig();

  useEffect(() => {
    if (!error) {
      xsollaAnalytics.sendOpen('login_pa_module__callback_url_open', projectId);
    }
  }, [error]);

  if (!currentProject || currentProjectFetching) {
    return <Preloader />;
  }

  if (error) {
    return <ToDashboardError pageName="callback_url_open" error={error} />;
  }

  const onSubmit = async (values: LoginProjectConfiguration) => {
    try {
      setSubmitError(false);

      if (values.default_login_url && !values.login_urls.includes(values.default_login_url)) {
        values.login_urls.push(values.default_login_url);
      }

      const sanitizedLoginUrls = values.login_urls.map((url) =>
        url.endsWith('/') ? url.slice(0, -1) : url
      );

      if (currentProject?.login_urls !== values?.login_urls) {
        enableChecklistItem(ChecklistItems.SET_CALLBACK_URL);
      }
      await updateCurrentProject({ ...values, login_urls: sanitizedLoginUrls });
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
      setSubmitError(true);
    }
  };

  return (
    <ConfigurationSample
      currentProject={currentProject}
      currentProjectType={currentProjectType}
      currentProjectFetching={currentProjectFetching}
      currentUpdateFetching={currentUpdateFetching}
      onSubmit={onSubmit}
      title={intl.formatMessage({ id: 'configuration-page.callback-urls.page-title' })}
      description={intl.formatMessage({ id: 'configuration-page.callback-urls.page-description' })}
      validate={calbackURLValidatation}
      submitError={submitError}>
      <CallbackUrlSettings />
    </ConfigurationSample>
  );
};

export default withRouter(CallbackUrlSettingsPage);
